import {
  Component,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { NgSelectConfig, NgSelectComponent } from '@ng-select/ng-select';
import utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
})
export class SelectComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() items: SelectItem[] = [];
  @Input() multiple: boolean = false;
  @Input() selected: string = '';
  @Input() multipleLines: boolean = false;

  @Input() id?: string = utils.makeID();
  @Input() name?: string;
  @Input() placeholder: string = 'Selecione';
  @Input() label?: string;
  @Input() type = 'text';

  // Form control
  @Input() controlName: string = '';
  @Input() group?: any;

  @Input() helpText?: string;
  @Input() errorText?: string = 'Campo inválido';

  @Input() readOnly?: boolean = false;
  @Input() validated: boolean = false;
  @Input() dropdownPosition?: 'bottom' | 'top' | 'auto';

  @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent;

  error: boolean = false;

  visibleItems: SelectItem[] = [];
  private isDropdownOpen: boolean = false;
  private shouldCloseImmediately: boolean = false;

  constructor(config: NgSelectConfig) {
    config.notFoundText = 'Nenhum item encontrado';

    config.bindLabel = 'label';
    config.bindValue = 'value';
  }

  ngOnInit(): void {
    this.visibleItems = this.items;
  }

  ngOnChanges(): void {
    this.visibleItems = this.items;
  }

  getClasses(): string {
    if (this.error) {
      return 'error';
    }

    if (this.validated) {
      return 'validated';
    }

    return '';
  }

  getTouched(): boolean {
    let touched = false;

    if (this.group) {
      const control = this.group.controls[this.controlName];

      if (control) {
        if (control.touched) {
          touched = true;
        }
      } else {
        console.error(`Control of ${this.controlName} not found!`);
      }
    }

    return touched;
  }

  getInvalid(): boolean {
    let invalid = false;

    if (this.group) {
      const control = this.group.controls[this.controlName];

      if (control.invalid) {
        invalid = true;
      }
    }

    return invalid;
  }

  getRequired(): boolean {
    const control = this.group.controls[this.controlName];

    if (control && control.validator) {
      let required = false;

      const validator = control.validator({} as AbstractControl);

      if (validator && validator.required) {
        required = true;
      }

      return required;
    }
    return false;
  }

  getError(): boolean {
    const control = this.group?.controls[this.controlName];
    // Mostra erro se o campo foi alterado (dirty) e está inválido
    return control && (control.dirty || control.touched) && this.getInvalid();
  }

  getErrorMessage(): string {
    if (!this.group) {
      return this.errorText || 'Campo inválido';
    }

    const control = this.group.controls[this.controlName];
    if (!control || !control.errors) {
      return this.errorText || 'Campo inválido';
    }

    const errors = control.errors;

    if (errors['required']) {
      // Verifica se é o campo de fundo de interesse
      if (this.controlName === 'fund') {
        return 'Selecione um fundo de interesse';
      }
      return 'Campo obrigatório';
    }

    // Tratamento específico para erro de quantidade de assinantes
    if (errors['VerifyLengthArray']) {
      let quantityField = '';
      let fieldLabel = '';

      if (this.controlName === 'necessary_signatures') {
        quantityField = 'necessary_signatures_quantity';
        fieldLabel = 'Qt. mín. de assinaturas necessárias';
      } else if (this.controlName === 'required_signatures') {
        quantityField = 'required_signatures_quantity';
        fieldLabel = 'Qt. mín. de assinaturas obrigatórias';
      }

      if (quantityField && this.group.controls[quantityField]) {
        const quantity = this.group.controls[quantityField].value;
        const quantityNumber = quantity
          ? Number(quantity.toString().replace(/\D/g, ''))
          : 0;

        if (quantityNumber > 0) {
          return `O número de pessoas selecionadas deve ser igual ao número informado no campo "${fieldLabel}" (${quantityNumber} pessoa${
            quantityNumber > 1 ? 's' : ''
          })`;
        }
      }

      return 'O número de pessoas selecionadas não corresponde à quantidade mínima informada';
    }

    return this.errorText || 'Campo inválido';
  }

  onOpen() {
    // Se foi marcado para fechar imediatamente, fecha
    if (this.shouldCloseImmediately) {
      this.shouldCloseImmediately = false;
      setTimeout(() => {
        if (this.ngSelect && this.ngSelect.isOpen) {
          this.ngSelect.close();
        }
      }, 10);
      return;
    }
    this.isDropdownOpen = true;
  }

  onClose() {
    this.isDropdownOpen = false;
    this.shouldCloseImmediately = false;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.ngSelect) {
        const selectElement = this.ngSelect.element;
        const control = selectElement.querySelector('.ng-select-container');

        if (control) {
          // Usa mousedown para capturar antes do ng-select processar
          control.addEventListener(
            'mousedown',
            (event: MouseEvent) => {
              if (this.readOnly) {
                return;
              }

              // Verifica se o clique foi no dropdown (não no controle)
              const target = event.target as HTMLElement;
              const isDropdownClick = target.closest('.ng-dropdown-panel');
              const isClearButton = target.closest('.ng-clear-wrapper');

              // Ignora se foi no botão de limpar ou no dropdown
              if (isDropdownClick || isClearButton) {
                return;
              }

              // Verifica o estado REAL do ng-select ANTES do clique processar
              const wasOpen = this.ngSelect.isOpen;

              // Se já estava aberto, previne o comportamento padrão e fecha
              if (wasOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.ngSelect.close();
              }
              // Se não estava aberto, deixa o comportamento padrão abrir
            },
            true,
          ); // Capture phase para interceptar antes
        }
      }
    }, 0);
  }
}
